import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import {
  Edit3,
  Building2,
  Users,
  Truck,
  MapPin,
  Clock,
  Phone,
  Mail,
  Calendar,
  Navigation,
  AlertCircle,
  CheckCircle2,
  Mic,
  Loader2
} from 'lucide-react';
import { getAmbulanceTypeAbbreviation } from '../../utils/ambulanceTypeHelper';

export const Step5ReviewSubmit = () => {
  const navigate = useNavigate();
  const {
    formData,
    setCurrentStep,
    updateFields,
    goBack,
    submitApplication,
    saveAndExit,
    isSaving
  } = useHospitalOnboarding();

  const [confirmed, setConfirmed] = useState(formData.confirmed_accurate || false);
  const [validationError, setValidationError] = useState('');

  const handleConfirmationChange = (e) => {
    const val = e.target.checked;
    setConfirmed(val);
    updateFields({ confirmed_accurate: val });
    if (val) setValidationError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmed) {
      setValidationError('Please review and check the authorization attestation box before submitting.');
      return;
    }

    const result = await submitApplication();
    if (result) {
      if (typeof window !== 'undefined' && window.__ccOnboardingSubmitted) {
        window.__ccOnboardingSubmitted(result);
        return;
      }
      navigate('/hospital/onboarding/submitted');
    }
  };

  const contacts = Array.isArray(formData.contacts)
    ? formData.contacts.filter((c) => c.name || c.mobile || c.email || c.role)
    : [];

  const monthlyDemands = Array.isArray(formData.monthly_demands)
    ? formData.monthly_demands.filter((m) => m.month || m.total || m.ambulance_type)
    : [];

  const areaDemands = Array.isArray(formData.area_demands)
    ? formData.area_demands.filter((a) => a.area || a.pincode || a.calls_per_month)
    : [];

  const destinationCorridors = Array.isArray(formData.destination_corridors)
    ? formData.destination_corridors.filter((d) => d.destination || d.distance_km || d.trips_per_month)
    : [];

  const hospitalTypes = Array.isArray(formData.hospital_types) ? formData.hospital_types : [];
  const emergencyFacilities = Array.isArray(formData.emergency_facilities) ? formData.emergency_facilities : [];
  const peakTimeSlots = Array.isArray(formData.peak_time_slots) ? formData.peak_time_slots : [];
  const serviceGaps = Array.isArray(formData.service_gaps) ? formData.service_gaps : [];

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 relative pb-4 border-b border-slate-200">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 5 OF 5
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Review & submit
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Review your hospital profile, emergency facilities, ambulance requirements, and service corridors before final submission.
          </p>
          {/* Smart Ambulance Blue Accent Line */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=hospital&step=5')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-xs font-medium text-slate-700 shadow-2xs transition-all cursor-pointer self-start sm:self-auto"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* CARD 1: Hospital Profile Summary */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <Building2 className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Hospital profile</h3>
                <p className="text-[11px] text-slate-400">Institutional credentials, classification, beds, and location</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 text-xs">
            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Hospital Name</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.hospital_name || '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Location (City, State, PIN)</span>
              <span className="font-semibold text-slate-800 text-sm">
                {[formData.city, formData.state].filter(Boolean).join(', ') || '—'} {formData.pincode ? `(${formData.pincode})` : ''}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Total Inpatient Beds</span>
              <span className="font-semibold text-slate-800 text-sm">
                {formData.number_of_beds || formData.total_beds ? `${formData.number_of_beds || formData.total_beds} Beds` : '—'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Location / Coordinates</span>
              <span className="font-semibold text-slate-800 text-sm font-mono flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span className="truncate">{formData.google_coordinates || '—'}</span>
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Emergency Setup</span>
              <span className="font-semibold text-slate-800 text-sm">
                {emergencyFacilities.length > 0 ? `${emergencyFacilities.length} Facilities Active` : 'Standard Setup'}
              </span>
            </div>

            <div>
              <span className="text-slate-400 block text-[11px] font-medium">Primary Contact / Admin</span>
              <span className="font-semibold text-slate-800 text-sm">
                {contacts.find((c) => c.is_primary === 'Yes')?.name || contacts[0]?.name || '—'}
              </span>
            </div>
          </div>

          {hospitalTypes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <span className="text-[11px] font-semibold text-slate-500 block mb-2">Hospital Classifications / Specialities:</span>
              <div className="flex flex-wrap gap-1.5">
                {hospitalTypes.map((type) => (
                  <span key={type} className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                    {type}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CARD 2: Facilities & Key Contacts */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Facilities & key contacts</h3>
                <p className="text-[11px] text-slate-400">Emergency departments, critical care units, and clinical leadership</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          {/* Top Capacity Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/80">
              <span className="text-slate-400 block text-[11px] font-medium">ICU Beds</span>
              <span className="text-lg font-bold text-slate-800">
                {formData.icu_beds ? `${formData.icu_beds} Beds` : '—'}
              </span>
            </div>

            <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/80">
              <span className="text-slate-400 block text-[11px] font-medium">Emergency Beds</span>
              <span className="text-lg font-bold text-slate-800">
                {formData.emergency_beds ? `${formData.emergency_beds} Beds` : '—'}
              </span>
            </div>

            <div className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-200/80">
              <span className="text-slate-400 block text-[11px] font-medium">Total Registered Beds</span>
              <span className="text-lg font-bold text-slate-800">
                {formData.total_beds || formData.number_of_beds ? `${formData.total_beds || formData.number_of_beds} Beds` : '—'}
              </span>
            </div>
          </div>

          {emergencyFacilities.length > 0 && (
            <div className="mb-5 pb-5 border-b border-slate-100">
              <span className="text-[11px] font-semibold text-slate-500 block mb-2">Emergency Facilities Available:</span>
              <div className="flex flex-wrap gap-1.5">
                {emergencyFacilities.map((fac) => (
                  <span key={fac} className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                    {fac}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <span className="text-[11px] font-semibold text-slate-500 block mb-2.5">Registered Key Contacts:</span>
            {contacts.length === 0 ? (
              <div className="p-4 bg-slate-50/60 rounded-xl border border-dashed border-slate-200 text-xs text-slate-400 text-center">
                No key contacts registered yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {contacts.map((c, i) => (
                  <div key={i} className="p-4 bg-slate-50/70 rounded-2xl border border-slate-200/80 text-xs hover:border-slate-300 transition-colors">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-xs shrink-0">
                          {c.name ? c.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                          <span className="font-semibold text-slate-800 text-sm block leading-tight">{c.name || 'Unnamed Contact'}</span>
                          <span className="text-slate-500 text-[11px] font-medium">{c.role || 'Designation unstated'}</span>
                        </div>
                      </div>
                      {c.is_primary === 'Yes' && (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 shrink-0">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="space-y-1.5 pt-2 border-t border-slate-200/60 text-slate-600 font-mono text-[11px]">
                      {c.mobile && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span>{c.mobile}</span>
                        </div>
                      )}
                      {c.email && (
                        <div className="flex items-center gap-2 font-sans truncate">
                          <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate">{c.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CARD 3: Ambulance Fleet Requirements & Demand */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <Truck className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Ambulance fleet requirements</h3>
                <p className="text-[11px] text-slate-400">Peak operational demand hours, monthly transfer volume, and dispatch deficits</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(3)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          {/* Peak Time Slots Chips */}
          <div className="mb-5 pb-5 border-b border-slate-100">
            <span className="text-[11px] font-semibold text-slate-500 block mb-2">Peak Emergency Time Slots:</span>
            <div className="flex flex-wrap gap-2">
              {peakTimeSlots.length > 0 ? (
                peakTimeSlots.map((slot) => (
                  <span key={slot} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                    <Clock className="w-3.5 h-3.5 text-blue-500" />
                    <span>{slot}</span>
                  </span>
                ))
              ) : (
                <span className="text-slate-400 text-xs">No standard peak slots selected</span>
              )}
              {formData.custom_peak_time && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-100">
                  <Clock className="w-3.5 h-3.5 text-indigo-500" />
                  <span>Custom: {formData.custom_peak_time}</span>
                </span>
              )}
            </div>
          </div>

          {/* Monthly Demand Logs Breakdown */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[11px] font-semibold text-slate-500">Monthly Ambulance Demand Logs:</span>
              <span className="text-xs font-semibold text-blue-600">
                {monthlyDemands.length} month(s) recorded
              </span>
            </div>

            {monthlyDemands.length === 0 ? (
              <div className="p-4 bg-slate-50/60 rounded-xl border border-dashed border-slate-200 text-xs text-slate-400 text-center">
                No monthly demand logs recorded
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider text-[11px] font-semibold">
                    <tr>
                      <th className="py-2.5 px-3">Month</th>
                      <th className="py-2.5 px-3">Ambulance Type</th>
                      <th className="py-2.5 px-3 text-right">Total Calls</th>
                      <th className="py-2.5 px-3 text-right">Inbound</th>
                      <th className="py-2.5 px-3 text-right">Outbound</th>
                      <th className="py-2.5 px-3 text-right">Unserved / Deficit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {monthlyDemands.map((m, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="py-2.5 px-3 font-semibold text-slate-800">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{m.month || '—'}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 font-semibold text-[11px] border border-blue-100">
                            {getAmbulanceTypeAbbreviation(m.ambulance_type) || m.ambulance_type || 'General'}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-right font-medium text-slate-800">{m.total || '0'}</td>
                        <td className="py-2.5 px-3 text-right">{m.inbound || '0'}</td>
                        <td className="py-2.5 px-3 text-right">{m.outbound || '0'}</td>
                        <td className="py-2.5 px-3 text-right">
                          {m.unserved && m.unserved !== '0' ? (
                            <span className="text-amber-700 font-semibold">{m.unserved}</span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* CARD 4: Service Areas & Corridors */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                <MapPin className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Service areas & corridors</h3>
                <p className="text-[11px] text-slate-400">Primary locality catchment, inter-facility routes, and operational bottlenecks</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCurrentStep(4)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-600 cursor-pointer transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          </div>

          {/* Area Demands Table / Breakdown */}
          <div className="mb-5 pb-5 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[11px] font-semibold text-slate-500">Key Coverage Localities & PIN Codes:</span>
              <span className="text-xs font-semibold text-blue-600">
                {areaDemands.length} zone(s) recorded
              </span>
            </div>

            {areaDemands.length === 0 ? (
              <div className="p-4 bg-slate-50/60 rounded-xl border border-dashed border-slate-200 text-xs text-slate-400 text-center">
                No coverage areas entered
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider text-[11px] font-semibold">
                    <tr>
                      <th className="py-2.5 px-3">Area / Catchment Locality</th>
                      <th className="py-2.5 px-3">PIN Code</th>
                      <th className="py-2.5 px-3 text-right">Calls / Month</th>
                      <th className="py-2.5 px-3 text-right">Inbound %</th>
                      <th className="py-2.5 px-3">Primary Condition</th>
                      <th className="py-2.5 px-3">Supply Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {areaDemands.map((a, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="py-2.5 px-3 font-semibold text-slate-800">
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                            <span>{a.area || '—'}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 font-mono text-slate-600">{a.pincode || '—'}</td>
                        <td className="py-2.5 px-3 text-right font-medium text-slate-800">{a.calls_per_month || '—'}</td>
                        <td className="py-2.5 px-3 text-right">{a.inbound_percent ? `${a.inbound_percent}%` : '—'}</td>
                        <td className="py-2.5 px-3">
                          {a.primary_type ? (
                            <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[11px] font-medium">
                              {a.primary_type}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 px-3">
                          {a.current_supply ? (
                            <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-[11px] font-medium border border-blue-100">
                              {a.current_supply}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Destination Corridors */}
          <div className="mb-5 pb-5 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[11px] font-semibold text-slate-500">Frequent Destination / Inter-facility Corridors:</span>
              <span className="text-xs font-semibold text-blue-600">
                {destinationCorridors.length} route(s) recorded
              </span>
            </div>

            {destinationCorridors.length === 0 ? (
              <div className="p-4 bg-slate-50/60 rounded-xl border border-dashed border-slate-200 text-xs text-slate-400 text-center">
                No destination corridors entered
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {destinationCorridors.map((d, idx) => (
                  <div key={idx} className="p-3.5 bg-slate-50/70 rounded-xl border border-slate-200/80 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                        <Navigation className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-slate-800 text-xs block">{d.destination || 'Unnamed Route'}</span>
                        <span className="text-[11px] text-slate-400">
                          {d.distance_km ? `${d.distance_km} km` : 'Standard Corridor'}
                        </span>
                      </div>
                    </div>
                    {d.trips_per_month && (
                      <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-[11px] font-semibold border border-blue-100">
                        {d.trips_per_month} trips/mo
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Service Gaps & Bottlenecks */}
          <div>
            <span className="text-[11px] font-semibold text-slate-500 block mb-2">Key Bottlenecks & Operational Gaps:</span>
            {serviceGaps.length === 0 && !formData.service_gap_details ? (
              <p className="text-xs text-slate-400 italic">No specific service gaps reported.</p>
            ) : (
              <div className="space-y-3">
                {serviceGaps.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {serviceGaps.map((gap) => (
                      <span key={gap} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                        <span>{gap}</span>
                      </span>
                    ))}
                  </div>
                )}
                {formData.service_gap_details && (
                  <div className="p-3.5 bg-slate-50/90 rounded-xl border border-slate-200/80 text-xs text-slate-700 italic">
                    "{formData.service_gap_details}"
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Simple Attestation Checkbox Line */}
        <div className="pt-2 px-1">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={handleConfirmationChange}
              className="mt-0.5 w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer shrink-0"
            />
            <span className="text-xs sm:text-sm text-slate-700 leading-normal">
              <strong className="font-semibold text-slate-800">Accuracy & Authorization:</strong>{' '}
              I hereby certify that all information submitted in this hospital onboarding application is authentic, accurate, and provided on behalf of the healthcare institution.
            </span>
          </label>

          {validationError && (
            <p className="text-xs text-red-600 font-medium mt-2 pl-7">
              {validationError}
            </p>
          )}
        </div>

        {/* Navigation Action Buttons with Blue Theme Matching Fleet */}
        <div className="flex items-center justify-between pt-6 border-t border-slate-200">
          <button
            type="button"
            onClick={goBack}
            className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-medium transition-all cursor-pointer"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs sm:text-sm font-medium transition-all cursor-pointer"
            >
              Save & exit
            </button>

            <button
              type="submit"
              disabled={isSaving}
              className="px-7 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs sm:text-sm shadow-md hover:shadow-lg transition-all flex items-center gap-2 cursor-pointer disabled:opacity-75 disabled:cursor-wait"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>{formData.status === 'SUBMITTED' ? 'Saving Updates...' : 'Submitting Hospital Application...'}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{formData.status === 'SUBMITTED' ? 'Save & Update Application' : 'Submit Hospital Application'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step5ReviewSubmit;
